// Google reCAPTCHA Enterprise score-based loader.
// Loads the per-site reCAPTCHA script, then exposes token retrieval used by
// protected requests (login, signup, createBooking, createPayment).
// Apps opt in by calling initRecaptcha(siteKey) at boot; apps that never init
// (currently skytree) get undefined tokens.
import { RecaptchaAction } from '@linktivity/ars-neo';

const SCRIPT_ID = 'recaptcha-script';
// Upper bound on how long callers wait for the reCAPTCHA script/token: a
// blocked or slow Google endpoint delays login/booking by at most this long.
const TOKEN_TIMEOUT_MS = 5000;

interface GrecaptchaEnterprise {
  ready(callback: () => void): void;
  execute(siteKey: string, options: { action: string }): Promise<string>;
}

interface Grecaptcha {
  enterprise?: GrecaptchaEnterprise;
}

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

// Action names recorded on the Google-side tokens; the auth service asserts
// them when assessing, so keep this mapping in sync with the backend.
const GOOGLE_ACTIONS: Partial<Record<RecaptchaAction, string>> = {
  [RecaptchaAction.RecaptchaActionLogin]: 'login',
  [RecaptchaAction.RecaptchaActionSignup]: 'signup',
  [RecaptchaAction.RecaptchaActionPlaceOrder]: 'place_order',
  [RecaptchaAction.RecaptchaActionPayment]: 'payment'
};

let initialized = false;
let recaptchaSiteKey = '';
let scriptReady: Promise<boolean> | null = null;

/**
 * Load reCAPTCHA Enterprise for the given site key. Safe to call once at app boot.
 * No-op outside the browser (SSR / prerender) or without a site key.
 */
export function initRecaptcha(siteKey?: string): void {
  if (initialized) {
    return;
  }
  // No-op (without marking initialized) when unconfigured or off-browser, so a
  // later call with a valid siteKey — e.g. a different boot path — still runs.
  if (!siteKey || typeof document === 'undefined') {
    return;
  }
  initialized = true;
  recaptchaSiteKey = siteKey;

  // Reuse an existing tag (e.g. HMR re-evaluating this module): grecaptcha
  // may already be available, otherwise wait for the pending load to settle.
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    scriptReady = window.grecaptcha?.enterprise
      ? Promise.resolve(true)
      : new Promise(resolve => {
          existing.addEventListener('load', () => resolve(true));
          existing.addEventListener('error', () => resolve(false));
        });
    return;
  }
  scriptReady = new Promise(resolve => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    // recaptcha.net serves the same script and stays reachable in regions
    // where google.com is blocked.
    script.src = `https://www.recaptcha.net/recaptcha/enterprise.js?render=${siteKey}`;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.appendChild(script);
  });
}

async function executeRecaptcha(
  action: RecaptchaAction
): Promise<string | undefined> {
  if (!initialized || typeof window === 'undefined') {
    return undefined;
  }
  const googleAction = GOOGLE_ACTIONS[action];
  if (!googleAction) {
    return undefined;
  }
  const loaded = scriptReady
    ? await scriptReady
    : !!window.grecaptcha?.enterprise;
  const enterprise = window.grecaptcha?.enterprise;
  if (!loaded || !enterprise) {
    return undefined;
  }
  return new Promise<string | undefined>(resolve => {
    enterprise.ready(() => {
      enterprise.execute(recaptchaSiteKey, { action: googleAction }).then(
        token => resolve(token),
        () => resolve(undefined)
      );
    });
  });
}

/**
 * reCAPTCHA token for the given action, or undefined when unavailable
 * (not initialized, script blocked, execution failed, or timed out).
 */
export function getRecaptchaToken(
  action: RecaptchaAction
): Promise<string | undefined> {
  return Promise.race([
    executeRecaptcha(action).catch(() => undefined),
    new Promise<undefined>(resolve =>
      setTimeout(() => resolve(undefined), TOKEN_TIMEOUT_MS)
    )
  ]);
}
